/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* istanbul ignore file */
import React from "react";
import { ReactElement } from "react";
import { Paragraph } from "../../components/Paragraph";
import {
  AanleidingHoofdstuk,
  DefaultHoofdstuk,
  DienstverleningHoofdstuk,
  StringStringTuple,
} from "../../../../.generated/rapportage/rapportagetypes";
import UnorderedList from "../../components/UnorderedList";
import Table from "../../components/Table";
import { Column } from "../../components/BaseTable";
import Heading from "../../components/Heading";
import { Chapter } from "../../components/Chapter";
import defaultTheme from "../../../../styles/themes/default";

type WensenEnDoelstellingenProps = {
  wensenEnDoelstellingen: DefaultHoofdstuk;
  aanleidingen: AanleidingHoofdstuk | null;
  dienstverlening: DienstverleningHoofdstuk | null;
};

type AanleidingenProps = {
  aanleidingen: AanleidingHoofdstuk;
};

type DienstverleningProps = {
  dienstverlening: DienstverleningHoofdstuk;
};

const AanleidingenChapter = ({
  aanleidingen,
}: AanleidingenProps): ReactElement => {
  const combinedVerwachtingen = Object.keys(
    aanleidingen.verwachtingenFinancieleToekomst || {}
  ).map((key) => {
    const tuple: StringStringTuple = aanleidingen.verwachtingenFinancieleToekomst![
      key as keyof typeof aanleidingen.verwachtingenFinancieleToekomst
    ];

    return `${tuple.item1} ${tuple.item2}`;
  });

  let aanleidingGesprek = ["Financiering  van de woning"];

  if (aanleidingen.aanleidingenGesprek) {
    aanleidingGesprek = [
      ...aanleidingGesprek,
      ...aanleidingen.aanleidingenGesprek,
    ];
  }

  return (
    <>
      <Heading text={aanleidingen.titel ?? ""} level={2} />

      <div style={{ marginBottom: defaultTheme.spacing?.xxl }}>
        <Paragraph
          noMarginBottom
          paragraaf={aanleidingen.paragrafen?.["p3030201"]}
        />
        <UnorderedList listId="aanleidingGesprek" entries={aanleidingGesprek} />
      </div>

      {aanleidingen.financieleWensenEnDoelen &&
        aanleidingen.financieleWensenEnDoelen.length > 0 && (
          <div style={{ marginBottom: defaultTheme.spacing?.xxl }}>
            <Paragraph
              noMarginBottom
              paragraaf={aanleidingen.paragrafen?.["p3030202"]}
            />
            <UnorderedList
              listId="financieleWensenEnDoelen"
              entries={aanleidingen.financieleWensenEnDoelen}
            />
          </div>
        )}

      {aanleidingen.verwachtingenFinancieleToekomst &&
        aanleidingen.verwachtingenFinancieleToekomst.length > 0 && (
          <div style={{ marginBottom: defaultTheme.spacing?.xxl }}>
            <Paragraph
              noMarginBottom
              paragraaf={aanleidingen.paragrafen?.["p3030203"]}
            />
            <UnorderedList
              listId="verwachtingen"
              entries={combinedVerwachtingen}
            />
          </div>
        )}
    </>
  );
};

const DienstVerleningChapter = ({
  dienstverlening,
}: DienstverleningProps): ReactElement => {
  let data: { key: string; value: number | undefined }[] = [];

  if (dienstverlening.advieskosten) {
    data = Object.keys(dienstverlening.advieskosten)?.map((key) => ({
      key,
      value: dienstverlening.advieskosten?.[key],
    }));
  }

  const columns: Column[] = [
    {
      accessor: "key",
      header: "Vergoeding",
    },
    {
      accessor: "value",
      header: "",
      dataType: "currency",
    },
  ];
  return (
    <>
      <Heading text="Dienstverlening" level={2} />

      <div style={{ marginBottom: defaultTheme.spacing?.xxl }}>
        <Paragraph
          noMarginBottom
          paragraaf={dienstverlening?.paragrafen?.["p3030301"]}
        />
        <UnorderedList
          listId="adviesOnderwerpen"
          entries={dienstverlening.adviesOnderwerpen}
        />
      </div>

      <div style={{ marginBottom: defaultTheme.spacing?.xxl }}>
        <Paragraph paragraaf={dienstverlening?.paragrafen?.["p3030302"]} />
        {data.length > 0 && (
          <Table
            data={data}
            columns={columns}
            extraStyle={`
            tbody tr:last-child td {
              background-color: ${defaultTheme.highlightRow.backgroundColor} !important;
              color: ${defaultTheme.highlightRow.color} !important;
              font-weight: ${defaultTheme.highlightRow.fontWeight} !important;
            }

            tbody tr:last-child td:first-child {
              border-top-left-radius: ${defaultTheme.highlightRow.borderRadius};
              border-bottom-left-radius: ${defaultTheme.highlightRow.borderRadius};
            }
            tbody tr:last-child td:last-child {
              border-top-right-radius: ${defaultTheme.highlightRow.borderRadius};
              border-bottom-right-radius: ${defaultTheme.highlightRow.borderRadius};
            }
          `}
          />
        )}
      </div>
    </>
  );
};

const WensenEnDoelstellingenChapter = ({
  wensenEnDoelstellingen,
  aanleidingen,
  dienstverlening,
}: WensenEnDoelstellingenProps): ReactElement => {
  return (
    <Chapter>
      <Heading text={wensenEnDoelstellingen.titel!} />
      <Paragraph paragraaf={wensenEnDoelstellingen.paragrafen?.["p30301"]}>
        {}
      </Paragraph>

      {aanleidingen && <AanleidingenChapter aanleidingen={aanleidingen} />}
      {dienstverlening && (
        <DienstVerleningChapter dienstverlening={dienstverlening} />
      )}
    </Chapter>
  );
};

export default WensenEnDoelstellingenChapter;
